// agate — the shared primitives of this console's design language, built on
// corenet's tokens/components (see vendor/corenet/CLAUDE.md). One
// implementation of each pattern; pages compose these instead of
// hand-rolling variants.
import { Link } from "react-router-dom";
import { Info, ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import { Popover, theme } from "antd";
import Card from "@/components/Card";

// HintIcon — the ⓘ hover-hint icon used next to a heading/label, backed by a
// Popover (not Tooltip): every real caller's hint is a full explanatory
// sentence, not a short label, and Popover's white card handles wrapped prose
// far better than Tooltip's one-line dark bubble. This was hand-rolled four
// separate times across the app before consolidating here (this file's
// SectionTitle/StatCard, plus Policy.tsx's own KpiTitle/SectionHeading) — two
// of the four on Tooltip, two already on Popover. One implementation now.
export function HintIcon({ hint, maxWidth = 320 }: { hint: string; maxWidth?: number }) {
  const { token } = theme.useToken();
  return (
    <Popover content={<div style={{ maxWidth, fontSize: token.fontSizeSM }}>{hint}</div>}>
      <span className="cursor-help text-text-muted">
        <Info size={13} aria-label={hint} />
      </span>
    </Popover>
  );
}

// SectionTitle — serif card heading with optional ⓘ hint and hover arrow.
export function SectionTitle({
  children,
  hint,
  to,
  linked,
  className,
}: {
  children: ReactNode;
  hint?: string;
  to?: string;
  linked?: boolean;
  className?: string;
}) {
  const body = (
    <h2
      className={`mb-3 flex items-center gap-1.5 font-serif text-base font-semibold text-text-primary ${className ?? ""}`}
    >
      {children}
      {hint && <HintIcon hint={hint} />}
      {linked && (
        <ArrowUpRight
          size={14}
          aria-hidden
          className="ml-auto text-text-muted opacity-0 transition-opacity group-hover:opacity-100"
        />
      )}
    </h2>
  );
  if (to) {
    return (
      <Link to={to} className="group block">
        {body}
      </Link>
    );
  }
  return body;
}

// StatCard — Agate stat tile: muted label + ⓘ, 22px number, muted annotation.
// Semantic color on the number ONLY when the number itself carries meaning.
export function StatCard({
  label,
  hint,
  value,
  annotation,
  tone,
  loading,
  to,
  onClick,
}: {
  label: string;
  hint: string;
  value: ReactNode;
  annotation?: string;
  tone?: "danger" | "warning" | "success" | "governance" | "accent";
  loading?: boolean;
  to?: string;
  onClick?: () => void;
}) {
  const valueColor =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : tone === "success"
          ? "text-success"
          : tone === "accent"
            ? "text-accent"
            : tone === "governance"
              ? "text-governance"
              : "text-text-primary";
  const interactive = Boolean(to || onClick);
  const body = (
    <Card
      className={`group h-full p-5 ${interactive ? "transition-colors hover:bg-surface-raised" : ""}`}
    >
      <div className="flex items-center gap-1.5 text-sm text-text-secondary">
        {label}
        <HintIcon hint={hint} />
        {interactive && (
          <ArrowUpRight
            size={13}
            aria-hidden
            className="ml-auto text-text-muted opacity-0 transition-opacity group-hover:opacity-100"
          />
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`text-[22px] font-semibold leading-tight tabular-nums ${valueColor}`}>
          {loading ? "…" : value}
        </span>
        {annotation && <span className="text-xs text-text-muted">{annotation}</span>}
      </div>
    </Card>
  );
  if (to) {
    return (
      <Link to={to} className="block">
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left">
        {body}
      </button>
    );
  }
  return body;
}

// LinkCard — a Card wrapped in a router Link so a whole tile deep-links.
export function LinkCard({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="group block h-full">
      <Card className="h-full transition-colors hover:bg-surface-raised">{children}</Card>
    </Link>
  );
}

// SeverityText — Agate severity rendering: colored uppercase text, no pill.
// Wording must stay distinct per tone (color-only signals fail color-blind
// users).
export function SeverityText({
  tone,
  children,
}: {
  tone: "danger" | "warning" | "info" | "governance" | "muted";
  children: ReactNode;
}) {
  const color =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : tone === "governance"
          ? "text-governance"
          : tone === "muted"
            ? "text-text-muted"
            : "text-info";
  return (
    <span className={`text-xs font-medium uppercase tracking-wide ${color}`}>{children}</span>
  );
}

// StateText — lifecycle state as quiet colored text (not a pill).
export function StateText({ state }: { state: string }) {
  const label =
    state === "active"
      ? "Active"
      : state === "stopped"
        ? "Stopped"
        : state === "provisioning"
          ? "Provisioning"
          : state === "failed"
            ? "Failed"
            : state;
  const color =
    state === "active"
      ? "text-success"
      : state === "provisioning"
        ? "text-warning"
        : state === "failed"
          ? "text-danger"
          : "text-text-muted";
  return <span className={`text-xs font-medium ${color}`}>{label}</span>;
}

// Anchor — Agate entity anchor: muted small-caps kind + mono value.
// The universal entity affordance (HOST xps-rachel, SANDBOX sbx-emma,
// AGENT code-reviewer, SERVER github).
export function Anchor({
  kind,
  value,
  onClick,
}: {
  kind: string;
  value?: string | null;
  onClick?: () => void;
}) {
  if (!value) return <span className="text-xs text-text-muted">—</span>;
  const body = (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-text-muted">{kind}</span>
      <span className={`font-mono text-xs ${onClick ? "text-accent hover:text-accent-hover" : "text-text-secondary"}`}>
        {value}
      </span>
    </span>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="text-left">
        {body}
      </button>
    );
  }
  return body;
}

// CoverageText — coverage_state rendered in the severity-text style with
// the design doc's channel split: alert states hot, governed cool,
// governance vocabulary distinct from threat severity.
export function CoverageText({ state }: { state: string }) {
  switch (state) {
    case "governed":
      return <span className="text-xs font-medium uppercase tracking-wide text-success">Governed</span>;
    case "both_active":
      return <span className="text-xs font-medium uppercase tracking-wide text-info">Mixed use</span>;
    case "drifting":
      return <span className="text-xs font-medium uppercase tracking-wide text-warning">Drifting</span>;
    case "ungoverned_local":
      return <span className="text-xs font-medium uppercase tracking-wide text-danger">Ungoverned</span>;
    case "local_active":
      return <span className="text-xs font-medium uppercase tracking-wide text-warning">Laptop only</span>;
    case "dormant":
    default:
      return <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Dormant</span>;
  }
}

// RiskText — Trust Registry risk rating in the severity-text style.
export function RiskText({ risk }: { risk?: string }) {
  const r = (risk ?? "").toUpperCase();
  if (r === "CRITICAL" || r === "HIGH")
    return <span className="text-xs font-medium uppercase tracking-wide text-danger">{r}</span>;
  if (r === "MEDIUM")
    return <span className="text-xs font-medium uppercase tracking-wide text-warning">Medium</span>;
  if (r === "LOW")
    return <span className="text-xs font-medium uppercase tracking-wide text-success">Low</span>;
  return <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Unknown</span>;
}

// RowCountFooter — the quiet Agate table footer strip.
export function RowCountFooter({ count, noun = "row" }: { count: number; noun?: string }) {
  return (
    <div className="mt-1 rounded-b-xl border-t border-border bg-surface-raised/50 px-6 py-2 text-right text-xs text-text-muted">
      {count} {count === 1 ? noun : `${noun}s`}
    </div>
  );
}

// SegmentedBar — Drata-style three-state horizontal bar. Segments render
// in order; zero-width segments are skipped. Pass tokens-resolved colors.
export function SegmentedBar({
  segments,
  height = 8,
}: {
  segments: { value: number; color: string; label: string }[];
  height?: number;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  return (
    <div
      className="flex w-full overflow-hidden rounded-full bg-surface-raised"
      style={{ height }}
      role="img"
      aria-label={segments.map((s) => `${s.label}: ${s.value}`).join(", ")}
    >
      {total > 0 &&
        segments
          .filter((s) => s.value > 0)
          .map((s, i) => (
            <div
              key={i}
              title={`${s.label}: ${s.value}`}
              style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
            />
          ))}
    </div>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <div className="text-sm text-text-muted">{children}</div>;
}

export function Skeleton() {
  return <div className="h-24 animate-pulse rounded-lg bg-surface-raised" />;
}
